import { BareSimpleSailing, BareSimpleSailingDay } from '@go-shared/types/sailing';
import { SimpleTemplate } from '@go-shared/types/template';
import {
  TemplateFragmentType,
  StrategicLinkFragment,
  FRAGMENT_TYPES,
} from '@go-shared/types/templateFragment';
import { Dropdown, DropdownItem } from '@spark-labs/avocado-ui';
import React, { useState } from 'react';

import { BubbleButton, BubbleOverlay, BubbleTable } from '../BubbleUI';
import { BubbleTableFields, BubbleTableTemplate } from '../BubbleUI/BubbleTable';
import { FragmentBlockSync } from './TemplateLinkForm';
import { notUndefined } from '@go-shared/lib/functional';
import { User } from '@go-shared/types/user';
import { FormGenericContainer } from '../FormGenericContainer';
import { canBulkLinkTemplates, canLinkTemplates } from '@go-shared/lib/permissions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface SailingSummaryPageProps {
  user: User;
  sailing: BareSimpleSailing;
  templates: SimpleTemplate[];
  onTemplateNameClick?: (blockSync: FragmentBlockSync) => void;
  onGoNext?: () => void;
  onUnlinkTemplateFragments?: (linkFragments: StrategicLinkFragment[]) => Promise<void>;
  onSailingDayNameClick?: (sailing: BareSimpleSailing, sailingDay: BareSimpleSailingDay) => void;
  onCreateTemplateClick?: (sailing: BareSimpleSailing, sailingDay: BareSimpleSailingDay) => void;
  onClose?: () => void;
}

interface SailingSummaryPageTableItem {
  id: string;
  day: string;
  events?: FragmentBlockSync;
  venueData?: FragmentBlockSync;
  feed?: FragmentBlockSync;
  content?: FragmentBlockSync;
  _source: BareSimpleSailingDay;
}

export default function SailingSummaryPage(props: SailingSummaryPageProps) {
  const [isBusy, setIsBusy] = useState(false);

  const tableFields: Array<BubbleTableFields<SailingSummaryPageTableItem>> = [
    {
      key: 'day',
      thProps: { className: 'bg-navy text-white pl-4-i' },
      tdProps: { className: 'p-1-i relative' },
    },
    {
      key: 'events',
      thProps: { className: 'bg-navy text-white pl-4-i' },
      tdProps: { className: 'p-1-i relative' },
    },
    {
      key: 'venueData',
      thProps: { className: 'bg-navy text-white pl-4-i' },
      tdProps: { className: 'p-1-i relative' },
    },
    {
      key: 'content',
      label: 'Content',
      thProps: { className: 'bg-navy text-white pl-4-i' },
      tdProps: { className: 'p-1-i relative' },
    },
    {
      key: 'feed',
      label: 'Print Program',
      thProps: { className: 'bg-navy text-white pl-4-i' },
      tdProps: { className: 'p-1-i relative' },
    },
  ];

  async function handleOnDayDropdownSelect(itm: DropdownItem, item: SailingSummaryPageTableItem) {
    try {
      setIsBusy(true);

      const key = itm.key;

      switch (key) {
        case 'view':
          if (!props.onSailingDayNameClick) return;
          props.onSailingDayNameClick(props.sailing, item._source);
          break;
        case 'save':
          if (!props.onCreateTemplateClick) return;
          props.onCreateTemplateClick(props.sailing, item._source);
          break;
        case 'partial':
        case 'full':
          if (!props.onUnlinkTemplateFragments) return;

          const { templateFragments } = item._source;
          if (!templateFragments) return;

          const toUnlink = FRAGMENT_TYPES.map(type => {
            const templateId = templateFragments[type];

            if (!templateId) return undefined;

            return {
              sailingId: props.sailing._id,
              sailingDayId: item._source._id,
              templateId,
              unlinkStrategy: key,
              fragmentType: type,
            };
          }).filter(notUndefined);

          await props.onUnlinkTemplateFragments(toUnlink);
          break;
      }
    } finally {
      setIsBusy(false);
    }
  }

  async function onCellDropdownSelect(
    itm: DropdownItem,
    sync: FragmentBlockSync,
    day: BareSimpleSailingDay,
    type: TemplateFragmentType
  ) {
    try {
      setIsBusy(true);

      switch (itm.key) {
        case 'view':
          if (!props.onTemplateNameClick) return;
          props.onTemplateNameClick(sync);
          break;
        case 'partial':
        case 'full':
          if (!props.onUnlinkTemplateFragments) return;
          await props.onUnlinkTemplateFragments([
            {
              sailingId: props.sailing._id,
              sailingDayId: day._id,
              templateId: sync.templateId,
              unlinkStrategy: itm.key,
              fragmentType: type,
            },
          ]);
          break;
      }
    } finally {
      setIsBusy(false);
    }
  }

  const tableItems: SailingSummaryPageTableItem[] = props.sailing.days.map(day => {
    let eventsTemplate: FragmentBlockSync | undefined;
    let venueDataTemplate: FragmentBlockSync | undefined;
    let contentTemplate: FragmentBlockSync | undefined;
    let feedTemplate: FragmentBlockSync | undefined;

    const templateFragments = day.templateFragments || {};
    const templateHistory = day.templateHistory || {};

    const eventsTemplateId = templateFragments.events || templateHistory.events;
    const venueDataTemplateId = templateFragments.venueData || templateHistory.venueData;
    const contentTemplateId = templateFragments.content || templateHistory.content;
    const feedTemplateId = templateFragments.feed || templateHistory.feed;

    if (eventsTemplateId) {
      const et = props.templates.find(t => t._id === eventsTemplateId);

      if (et) {
        eventsTemplate = {
          type: 'events',
          text: et.name,
          templateId: et._id,
          active: !!templateFragments.events,
        };
      }
    }

    if (venueDataTemplateId) {
      const vdt = props.templates.find(t => t._id === venueDataTemplateId);
      if (vdt) {
        venueDataTemplate = {
          type: 'venueData',
          text: vdt.name,
          templateId: vdt._id,
          active: !!templateFragments.venueData,
        };
      }
    }

    if (contentTemplateId) {
      const ct = props.templates.find(t => t._id === contentTemplateId);

      if (ct) {
        contentTemplate = {
          type: 'content',
          text: ct.name,
          templateId: ct._id,
          active: !!templateFragments.content,
        };
      }
    }

    if (feedTemplateId) {
      const ft = props.templates.find(t => t._id === feedTemplateId);
      if (ft) {
        feedTemplate = {
          type: 'feed',
          text: ft.name,
          templateId: ft._id,
          active: !!templateFragments.feed,
        };
      }
    }

    return {
      id: day._id,
      day: day.name,
      events: eventsTemplate,
      venueData: venueDataTemplate,
      content: contentTemplate,
      feed: feedTemplate,
      _source: day,
    };
  });

  const tableTemplates: Array<BubbleTableTemplate<SailingSummaryPageTableItem>> = [
    {
      key: 'day',
      render: item => {
        const dropdownItems = [{ key: 'view', element: `View Day` }];

        const userCanEdit = canLinkTemplates(props.user);

        if (userCanEdit) {
          dropdownItems.push({ key: 'save', element: 'Create Template From Day' });

          if (!item._source.locked) {
            dropdownItems.push(
              { key: 'partial', element: `Unlink All Fragments Only*` },
              { key: 'full', element: `Unlink All Fragments & Clear*` }
            );
          }
        }

        return (
          <Dropdown items={dropdownItems} onSelect={itm => handleOnDayDropdownSelect(itm, item)}>
            {({ toggleRef, ...rest }) => (
              <div className="whitespace-nowrap">
                <BubbleButton
                  buttonRef={toggleRef}
                  {...rest}
                  variant="navy"
                  inverse
                  className="stretched-link whitespace-nowrap"
                  iconSuffix="caret-down"
                >
                  <span className="underline">{item.day}</span>
                </BubbleButton>
                {item._source.locked && <FontAwesomeIcon icon="lock" className="mr-2 text-smoke" />}
              </div>
            )}
          </Dropdown>
        );
      },
    },
    {
      key: 'events',
      render: item =>
        item.events ? (
          makeTdDropdown('events', item)
        ) : (
          <span className="text-smoke italic p-3-i">Not linked</span>
        ),
    },
    {
      key: 'venueData',
      render: item =>
        item.venueData ? (
          makeTdDropdown('venueData', item)
        ) : (
          <span className="text-smoke italic p-3-i">Not linked</span>
        ),
    },
    {
      key: 'content',
      render: item =>
        item.content ? (
          makeTdDropdown('content', item)
        ) : (
          <span className="text-smoke italic p-3-i">Not linked</span>
        ),
    },
    {
      key: 'feed',
      render: item =>
        item.feed ? (
          makeTdDropdown('feed', item)
        ) : (
          <span className="text-smoke italic p-3-i">Not linked</span>
        ),
    },
  ];

  function makeTdDropdown(type: TemplateFragmentType, item: SailingSummaryPageTableItem) {
    const sync = item[type];

    if (!sync) return;

    const dropdownItems = [{ key: 'view', element: 'View Template' }];

    const userCanEdit = canLinkTemplates(props.user);

    if (userCanEdit && !item._source.locked && sync.active) {
      dropdownItems.push(
        { key: 'partial', element: 'Unlink Only' },
        { key: 'full', element: 'Unlink & Clear' }
      );
    }

    return (
      <Dropdown
        items={dropdownItems}
        onSelect={itm => onCellDropdownSelect(itm, sync, item._source, type)}
      >
        {({ toggleRef, ...rest }) => (
          <BubbleButton
            buttonRef={toggleRef}
            {...rest}
            variant={!sync.active ? 'dirt' : 'velvet'}
            inverse
            className="stretched-link text-left p-3-i"
            icon={item._source.locked ? 'lock' : sync.active ? 'chain' : 'chain-broken'}
            iconSuffix="caret-down"
          >
            <span className="underline">{sync.text}</span>
          </BubbleButton>
        )}
      </Dropdown>
    );
  }

  return (
    <div className="tflf-2">
      <FormGenericContainer
        footer={() =>
          canBulkLinkTemplates(props.user) ? (
            <>
              <BubbleButton variant="navy" onClick={props.onGoNext}>
                Bulk Link Template Fragments
              </BubbleButton>
            </>
          ) : (
            <></>
          )
        }
      >
        <div className="flex items-start mb-5">
          <div>
            <h1 className="text-lg text-slate-dark font-semibold">{props.sailing.name}</h1>
            <h2 className="text-2xl text-navy">Template Summary</h2>
          </div>
          <BubbleButton
            onClick={props.onClose}
            className="ml-auto"
            variant="navy"
            inverse
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <BubbleOverlay loading={isBusy} className="overflow-x-auto">
          <BubbleTable
            fields={tableFields}
            items={tableItems}
            templates={tableTemplates}
            striped
            responsive
            keyExtractor={itm => itm.id}
          />
        </BubbleOverlay>
        <div className="text-slate italic mt-1">
          *Bulk operation may take a long time (up to a minute).
        </div>
      </FormGenericContainer>
    </div>
  );
}
